import React, { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { useUpdateProductMutation } from '@/apis/extended/productApi';
import { parseString } from '@/utils/helper';
import { Product } from '@/types/ProductType';

// Field constraints for validation and clamping
const CONSTRAINTS = {
  offerPrice: { min: 0, max: 100_000_000, step: 1 },
  hoaFees: { min: 0, max: 10_000, step: 1 },
  commission: { min: 0, max: 100, step: 0.5 },
  rent: { min: 0, max: 500_000, step: 1 },
  monthlyPropertyTaxes: { min: 0, max: 100_000, step: 1 },
  monthlyHomeownersInsurance: { min: 0, max: 50_000, step: 1 },
} as const;

const getSharedInputSx = (theme: Theme): SxProps<Theme> => ({
  '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
  '& .MuiInputBase-input': { color: theme.palette.text.primary },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.text.primary, 0.23),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.text.primary, 0.4),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '& .MuiFormHelperText-root': { color: theme.palette.text.secondary },
});

interface ProductDetailPropertySpecificsModalProps {
  product: Product;
  open: boolean;
  onClose: () => void;
}

export const ProductDetailPropertySpecificsModal = ({
  product,
  open,
  onClose,
}: ProductDetailPropertySpecificsModalProps) => {
  const theme = useTheme();
  const owner = product.user;
  const sharedInputSx = getSharedInputSx(theme);
  const panelSx = {
    background: 'var(--panel-tonal)',
    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
    borderRadius: 2,
  };

  const [updateProduct, { isLoading: isUpdatingProduct }] =
    useUpdateProductMutation();

  const [form, setForm] = useState({
    offerPrice: '',
    hoaFees: '',
    commission: 0,
    rent: '',
    propertyType: 'single_family', // single_family, multi_unit, investment
    monthlyPropertyTaxes: '',
    monthlyHomeownersInsurance: '',
  });

  useEffect(() => {
    setForm({
      offerPrice: parseString(product?.loanInfo?.offerPrice),
      hoaFees: parseString(product?.loanInfo?.hoaFees),
      commission: product?.loanInfo?.commission || 0,
      rent: parseString(product?.loanInfo?.rent),
      propertyType: product?.loanInfo?.propertyType || 'single_family',
      monthlyPropertyTaxes: parseString(
        product?.loanInfo?.monthlyPropertyTaxes
      ),
      monthlyHomeownersInsurance: parseString(
        product?.loanInfo?.monthlyHomeownersInsurance
      ),
    });
  }, [product]);

  const isUpdated = React.useMemo(() => {
    return (
      form.offerPrice !== product?.loanInfo?.offerPrice?.toString() ||
      form.hoaFees !== product?.loanInfo?.hoaFees?.toString() ||
      form.commission !== product?.loanInfo?.commission ||
      form.rent !== product?.loanInfo?.rent?.toString() ||
      form.propertyType !==
        (product?.loanInfo?.propertyType || 'single_family') ||
      form.monthlyPropertyTaxes !==
        product?.loanInfo?.monthlyPropertyTaxes?.toString() ||
      form.monthlyHomeownersInsurance !==
        product?.loanInfo?.monthlyHomeownersInsurance?.toString()
    );
  }, [form, product]);

  // Allow intermediate typing (e.g. "1500." or "1") for numeric string fields; no clamp on keystroke
  const isValidNumericInput = (v: string) =>
    v === '' || v === '.' || /^\d*\.?\d*$/.test(v);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'commission') {
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
      const v =
        value === '' ? 0 : Math.min(100, Math.max(0, Number(value) || 0));
      setForm({ ...form, commission: v });
      return;
    }

    const key = name as keyof typeof CONSTRAINTS;
    if (key in CONSTRAINTS) {
      if (!isValidNumericInput(value)) return;
      setForm({ ...form, [name]: value });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleSelectChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const clampForSave = (
    key: keyof typeof CONSTRAINTS,
    raw: string | number
  ): number | undefined => {
    const n = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (raw === '' || raw === undefined || !Number.isFinite(n))
      return undefined;
    const { min, max } = CONSTRAINTS[key];
    const clamped = Math.min(max, Math.max(min, n));
    return clamped;
  };

  const handleSave = () => {
    updateProduct({
      id: product._id,
      offerPrice: clampForSave('offerPrice', form.offerPrice),
      hoaFees: clampForSave('hoaFees', form.hoaFees),
      commission: clampForSave('commission', form.commission),
      rent: clampForSave('rent', form.rent),
      propertyType: form.propertyType,
      monthlyPropertyTaxes: clampForSave(
        'monthlyPropertyTaxes',
        form.monthlyPropertyTaxes
      ),
      monthlyHomeownersInsurance: clampForSave(
        'monthlyHomeownersInsurance',
        form.monthlyHomeownersInsurance
      ),
    });
  };

  const isDscrProgram = owner?.loanProgram === USER_LOAN_PROGRAM.DSCR;
  const showRentalIncome =
    isDscrProgram ||
    form.propertyType === 'investment' ||
    form.propertyType === 'multi_unit';

  const rentalIncomeLabel = useMemo(() => {
    if (isDscrProgram) return 'Monthly rental income (DSCR)';
    if (form.propertyType === 'multi_unit') return 'Monthly rent (all units)';
    return 'Projected monthly rental income';
  }, [isDscrProgram, form.propertyType]);

  const rentalIncomeHelper = useMemo(() => {
    if (isDscrProgram)
      return 'Used for DSCR ratio. Enter projected or actual monthly rent.';
    if (form.propertyType === 'multi_unit')
      return 'Total monthly rent from all units. Max $500,000.';
    return 'Projected monthly rental income. Max $500,000.';
  }, [isDscrProgram, form.propertyType]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: 'taste-rise taste-panel',
        sx: {
          background: '#ffffff',
          borderRadius: 3,
          boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.12)}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          background: '#ffffff',
          px: 3,
          py: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              className="font-display"
              sx={{ color: theme.palette.text.primary }}
            >
              Property Specifics
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mt: 0.25 }}
            >
              Adjust taxes, insurance & fees
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: theme.palette.text.primary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, background: '#ffffff' }}>
        <Alert
          severity="info"
          sx={{
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            color: theme.palette.text.primary,
            '& .MuiAlert-icon': { color: theme.palette.primary.main },
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary }}
          >
            Adjust property-specific details including taxes, insurance, and
            fees that affect your loan calculations and monthly payments.
            {isDscrProgram && (
              <>
                {' '}
                Enter <strong>monthly rental income</strong> below to drive your
                DSCR ratio and loan terms.
              </>
            )}
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Property Type */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Property Type</InputLabel>
              <Select
                name="propertyType"
                value={form.propertyType}
                label="Property Type"
                onChange={handleSelectChange}
              >
                <MenuItem value="single_family">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className="fa-light fa-home" />
                    Single Family Home
                  </Box>
                </MenuItem>
                <MenuItem value="multi_unit">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className="fa-light fa-building" />
                    Multi-Unit Property
                  </Box>
                </MenuItem>
                <MenuItem value="investment">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className="fa-light fa-chart-line" />
                    Investment Property
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Offer Price */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Offer Price"
              name="offerPrice"
              type="number"
              value={form.offerPrice}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.offerPrice.min,
                max: CONSTRAINTS.offerPrice.max,
                step: CONSTRAINTS.offerPrice.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText="Offer amount replaces list price in estimates"
              sx={sharedInputSx}
            />
          </Grid>

          {/* HOA Fees */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Monthly HOA Fees"
              name="hoaFees"
              type="number"
              value={form.hoaFees}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.hoaFees.min,
                max: CONSTRAINTS.hoaFees.max,
                step: CONSTRAINTS.hoaFees.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`$0 – $${CONSTRAINTS.hoaFees.max.toLocaleString(
                'en-US',
                { maximumFractionDigits: 0 }
              )} per month`}
              sx={sharedInputSx}
            />
          </Grid>

          {/* Buyers Agent Commission */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Buyers Agent Commission"
              name="commission"
              type="number"
              value={form.commission}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.commission.min,
                max: CONSTRAINTS.commission.max,
                step: CONSTRAINTS.commission.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      %
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`${CONSTRAINTS.commission.min}–${CONSTRAINTS.commission.max}%`}
              sx={sharedInputSx}
            />
          </Grid>

          {/* Monthly Property Taxes */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Monthly Property Taxes"
              name="monthlyPropertyTaxes"
              type="number"
              value={form.monthlyPropertyTaxes}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.monthlyPropertyTaxes.min,
                max: CONSTRAINTS.monthlyPropertyTaxes.max,
                step: CONSTRAINTS.monthlyPropertyTaxes.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`$0 – $${(
                CONSTRAINTS.monthlyPropertyTaxes.max / 1000
              ).toFixed(0)}k per month`}
              sx={sharedInputSx}
            />
          </Grid>

          {/* Monthly Homeowners Insurance */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Monthly Homeowners Insurance"
              name="monthlyHomeownersInsurance"
              type="number"
              value={form.monthlyHomeownersInsurance}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.monthlyHomeownersInsurance.min,
                max: CONSTRAINTS.monthlyHomeownersInsurance.max,
                step: CONSTRAINTS.monthlyHomeownersInsurance.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`$0 – $${(
                CONSTRAINTS.monthlyHomeownersInsurance.max / 1000
              ).toFixed(0)}k per month`}
              sx={sharedInputSx}
            />
          </Grid>

          {/* Monthly Rental Income – DSCR calculation + Investment / Multi-Unit */}
          {showRentalIncome && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={rentalIncomeLabel}
                name="rent"
                type="number"
                value={form.rent}
                onChange={handleInputChange}
                inputProps={{
                  min: CONSTRAINTS.rent.min,
                  max: CONSTRAINTS.rent.max,
                  step: CONSTRAINTS.rent.step,
                  inputMode: 'decimal',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        $
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                helperText={rentalIncomeHelper}
                sx={sharedInputSx}
              />
            </Grid>
          )}

          {/* Current Values Display */}
          <Grid item xs={12}>
            <Divider
              sx={{
                my: 2,
                borderColor: alpha(theme.palette.text.primary, 0.08),
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={600}
              gutterBottom
              sx={{ color: theme.palette.text.primary }}
            >
              Current Property Details
            </Typography>
            <Box sx={{ p: 2, ...panelSx }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Offer Price
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.text.primary }}
                  >
                    $
                    {product?.loanInfo?.offerPrice?.toLocaleString('en-US', {
                      maximumFractionDigits: 0,
                    }) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    List Price
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.text.primary }}
                  >
                    $
                    {product?.price?.toLocaleString('en-US', {
                      maximumFractionDigits: 0,
                    }) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Property Type
                  </Typography>
                  <Chip
                    label={form.propertyType
                      .replace('_', ' ')
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.text.primary,
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.15
                      )}`,
                    }}
                  />
                </Grid>
                {product?.loanInfo?.zipCode && (
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Location (Zip Code)
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {product.loanInfo.zipCode}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          background: '#ffffff',
          borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={isUpdatingProduct}
          disabled={!isUpdated}
          sx={{
            fontWeight: 700,
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
